/**
 * PINs are hashed on the device with PBKDF2-SHA256 (100k rounds) and a random salt.
 * The server stores only hash + salt; the till verifies offline against its local staff list.
 */
const ROUNDS = 100_000;
const b64 = (buf: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function derive(pin: string, salt: Uint8Array): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations: ROUNDS }, key, 256);
  return b64(bits);
}

export async function hashPin(pin: string): Promise<{ pin_hash: string; pin_salt: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { pin_hash: await derive(pin, salt), pin_salt: b64(salt) };
}

export async function verifyPin(pin: string, hash: string, salt: string): Promise<boolean> {
  const got = await derive(pin, unb64(salt));
  // constant-time-ish compare
  let diff = got.length ^ hash.length;
  for (let i = 0; i < Math.min(got.length, hash.length); i++) diff |= got.charCodeAt(i) ^ hash.charCodeAt(i);
  return diff === 0;
}

export const validPin = (pin: string) => /^\d{4,6}$/.test(pin);
